import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CmsDocument } from '@/lib/cms/section-types';
import { getCmsDocument, type CmsPageLocale, type CmsPageSlug } from '@/lib/db/cms-pages';

/**
 * Fetch the typed CMS document of a slug for the active i18n locale (L6.5).
 * Returns `null` while loading, when the page is absent, or when its metadata
 * is legacy/invalid — callers render their i18n fallback in all those cases,
 * mirroring the inline pattern of HeroCataloguePremium / AboutPage. Refetches
 * when the user switches language.
 */
export function useCmsDocument(slug: CmsPageSlug): CmsDocument | null {
  const { i18n } = useTranslation();

  const locale = (
    ['en', 'fr', 'ar'].includes(i18n.language?.split('-')[0] ?? '')
      ? (i18n.language.split('-')[0] as CmsPageLocale)
      : 'en'
  ) as CmsPageLocale;

  const [doc, setDoc] = useState<CmsDocument | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Drop a stale document synchronously when slug/locale changes (the new
    // locale may have no CMS row, in which case the fetch never sets state).
    // The functional no-op form keeps the initial null → null mount quiet.
    setDoc((prev) => (prev === null ? prev : null));
    getCmsDocument(slug, locale)
      .then((d) => {
        // Null docs never set state: callers fall back to i18n and the
        // post-resolution no-op would only trip test act() bookkeeping.
        if (!cancelled && d) setDoc(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug, locale]);

  return doc;
}
